import { Flex, useMantineTheme } from '@mantine/core';
import { observer } from 'mobx-react-lite';
import type { JSX } from 'react';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import type {
  Goal as GoalModel,
  IGetOrCreateGoalSegmentResult,
  IPredicate,
} from '@feathr/blackbox';
import { Radios } from '@feathr/components';
import { useStore } from '@feathr/extender/state';

import FormSelect from '../../FormSelect';
import { buildFormSubmissionPredicates } from '../Goal.predicates';
import GoalDevConsole from '../GoalDevConsole';

type TFormSelection = 'any' | 'specific';

interface IProps {
  goal: GoalModel;
}

function FormSubmissionGoal({ goal }: Readonly<IProps>): JSX.Element {
  const { t } = useTranslation();
  const { Segments } = useStore();
  const [selectMode, setSelectMode] = useState<TFormSelection>('any');
  const [selectedForms, setSelectedForms] = useState<string[]>([]);
  const [currentPredicates, setCurrentPredicates] = useState<IPredicate[]>([]);
  const [segmentResult, setSegmentResult] = useState<IGetOrCreateGoalSegmentResult | undefined>(
    undefined,
  );
  const {
    spacing: { '4': gap },
  } = useMantineTheme();

  useEffect(() => {
    async function updateSegment(): Promise<void> {
      const formIds = selectMode === 'specific' ? selectedForms : undefined;
      const predicates = buildFormSubmissionPredicates(formIds);
      const segmentName = `Form Submission Goal - ${goal.get('name') || 'Unnamed'}`;

      setCurrentPredicates(predicates);
      const result = await Segments.getOrCreateGoalSegment(predicates, segmentName);
      setSegmentResult(result);
      goal.set({ segment: result.segment.id });
    }

    if (selectMode === 'any' || (selectMode === 'specific' && selectedForms.length > 0)) {
      updateSegment();
    }
  }, [selectMode, selectedForms, Segments, goal]);

  function handleSelectMode(value?: string): void {
    if (!value) {
      return;
    }
    setSelectMode(value as TFormSelection);
  }

  const options = [
    { id: 'any', name: 'Any form submission' },
    { id: 'specific', name: 'Submissions on specific form(s)' },
  ];

  function handleFormSelection(value?: string[]): void {
    if (!value) {
      return;
    }
    setSelectedForms(value);
  }

  return (
    <Flex direction={'column'} gap={gap}>
      <Radios
        helpText={t('Choose which form submissions will be tracked as conversions.')}
        label={t('Form selection')}
        layout={'block'}
        onChange={handleSelectMode}
        options={options}
        value={selectMode}
      />
      {selectMode === 'specific' && (
        <FormSelect
          filters={{ state: 'published' }}
          isMulti={true}
          label={t('Form(s)')}
          onChange={handleFormSelection}
          placeholder={t('Start typing to search forms')}
          showLastModified={true}
          value={selectedForms}
        />
      )}
      <GoalDevConsole predicates={currentPredicates} segmentResult={segmentResult} />
    </Flex>
  );
}

export default observer(FormSubmissionGoal);
